import { useMemo, useState } from 'react';
import { getUserSub, logout } from './auth';
import { CONFIG } from './config';
import { runEvent, errMessage } from './pageUtils';
import { emitAcceptPlanningTerms, fetchTermsAcceptedVersion } from './api/account';
import { termsHtml, TERMS_VERSION } from './terms';

/** Shown after sign-in until the person accepts the current Terms of Use and Privacy Notice. */
export function TermsGate({ onAccepted }: { onAccepted: () => void }) {
  const html = useMemo(termsHtml, []);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await runEvent(connId => emitAcceptPlanningTerms(TERMS_VERSION, connId));
      onAccepted();
    } catch (e) {
      // The "done" message can go missing (a timeout or dropped connection) after the acceptance was in fact saved, so
      // ask the server before reporting a failure.
      const sub = getUserSub();
      const saved = sub ? await fetchTermsAcceptedVersion(sub).catch(() => null) : null;
      if (saved === TERMS_VERSION) { onAccepted(); return; }
      setError(errMessage(e, 'Something went wrong.'));
      setBusy(false);
    }
  }

  return (
    <div className="sx-root">
      <main className="sx-main">
        <div className="sx-form">
          <div>
            <h1 className="sx-h1">Before you continue</h1>
            <p className="sx-lede">Please read the Terms of Use and the Privacy Notice for Elevations Planning.</p>
          </div>
          <div className="sx-terms sx-prose sx-legal" dangerouslySetInnerHTML={{ __html: html }} />
          <label className="sx-check">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} disabled={busy} />
            <span>I agree to the Terms of Use and have read the Privacy Notice.</span>
          </label>
          {error && <div className="sx-err">Your acceptance wasn't saved: {error}</div>}
          <button type="button" className="sx-cta" disabled={!agreed || busy} onClick={() => void accept()}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
          <button type="button" className="sx-cta sx-cta-2" disabled={busy} onClick={() => logout(CONFIG.cognitoDomain, CONFIG.clientId)}>
            Sign out
          </button>
        </div>
      </main>
    </div>
  );
}
